import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { table_grid_columns as tableGridColumns } from '../assets/js/field_config/gantt-columns';
import { cleanColumnsIssues } from '../views/ganttContainer/gantt/viewUtils';
import { isColumnAvailableForRole } from '../utils/partialPermissionUtils';
import masterplanPermissions from './useMasterplanPermissions';
import * as ganttActions from '../redux/slices/ganttSlice';
import * as projectActions from '../redux/slices/projectSlice';

/**
 * Custom hook para configurar las columnas de la tabla Gantt
 * Organiza la lógica en funciones pequeñas y claras para mejor legibilidad
 */
export const useTableConfiguration = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  // Redux selectors
  const userPreferenceTableGantt = useSelector(
    (state) => state.ganttState.userPreferenceTableGantt
  );
  const activatedColumns = useSelector(
    (state) => state.ganttState.activatedColumns
  );
  const projectState = useSelector((state) => state.projectState);

  /**
   * Obtiene las columnas base del sistema
   * @returns {Array} Columnas por defecto del sistema
   */
  const getDefaultColumns = () =>
    tableGridColumns(null, t, masterplanPermissions);

  /**
   * Decide qué columnas usar: preferencias del usuario o columnas por defecto
   * @param {Array} defaultColumns - Columnas por defecto del sistema
   * @returns {Array} Columnas a utilizar
   */
  const selectColumnsToUse = (defaultColumns) => {
    const { gridTable } = userPreferenceTableGantt;
    const shouldUseDefaults =
      !userPreferenceTableGantt || !gridTable || !gridTable.length;
    return shouldUseDefaults ? [...defaultColumns] : gridTable;
  };

  /**
   * Limpia problemas en las columnas (undefined, duplicados, etc.)
   * @param {Array} columns - Columnas a limpiar
   * @returns {Array} Columnas limpiadas
   */
  const cleanColumns = (columns) => cleanColumnsIssues(columns);

  /**
   * Verifica si hay cambios en la propiedad 'filterable' entre columnas
   * @param {Array} cleanedColumns - Columnas limpiadas
   * @param {Array} defaultColumns - Columnas por defecto
   * @returns {boolean} True si hay cambios en filterable
   */
  const checkFilterableChanges = (cleanedColumns, defaultColumns) => {
    if (!cleanedColumns) return false;

    return cleanedColumns.some((column, index) => {
      if (defaultColumns[index]) {
        return column.filterable !== defaultColumns[index].filterable;
      }
      return false;
    });
  };

  /**
   * Restaura la propiedad filterable desde las columnas por defecto si es necesario
   * @param {Array} columns - Columnas a procesar
   * @param {Array} defaultColumns - Columnas por defecto
   * @param {boolean} hasFilterableChanges - Si hay cambios en filterable
   * @returns {Array} Columnas con filterable corregido
   */
  const restoreFilterableProperty = (
    columns,
    defaultColumns,
    hasFilterableChanges
  ) => {
    if (!hasFilterableChanges) return columns;
    return columns.map((column, index) => ({
      ...column,
      filterable: defaultColumns[index]?.filterable
    }));
  };

  /**
   * Aplica traducciones a las etiquetas de las columnas
   * @param {Array} columns - Columnas a traducir
   * @returns {Array} Columnas con etiquetas traducidas
   */
  const translateColumnLabels = (columns) => {
    return columns.map((column) => {
      if (column.name === 'buttons') {
        return column;
      }

      const isIconOrStringType =
        column.data_type.includes('/icon') ||
        column.data_type.includes('/string');

      if (isIconOrStringType) {
        // Traduce la etiqueta principal
        column.label = t('tables.gantt.' + column.name + '.label');

        // Traduce las opciones si no es array/string
        if (!column.data_type.includes('array/string') && column.from_values) {
          column.from_values = column.from_values.map((option) => ({
            ...option,
            label: t('tables.gantt.' + column.name + '.options.' + option.value)
          }));
        }
      } else {
        // Para otros tipos de datos, traduce directamente
        column.label = t('tables.gantt.' + column.name);
      }

      return column;
    });
  };

  /**
   * Actualiza el estado de Redux con las columnas procesadas
   * @param {Array} columns - Columnas procesadas
   */
  const updateReduxState = (columns) => {
    // Actualiza las preferencias de tabla en Redux
    dispatch(
      ganttActions.setUserPreferenceTableGantt({
        table: columns,
        ganttChart: null
      })
    );

    // Establece el criterio de estado del proyecto
    dispatch(
      projectActions.setProps({
        ...projectState.props,
        status_criteria: 'Baseline'
      })
    );
  };

  /**
   * Prepara las opciones de columnas para filtros y selección
   * @param {Array} columns - Columnas configuradas
   * @returns {Object} Opciones de columnas preparadas
   */
  const prepareColumnOptions = (columns) => {
    const availableOptions = [];
    const columnsToDisable = [];

    // Genera opciones disponibles para columnas
    columns.forEach((column) => {
      if (!column.ignore_as_column && isColumnAvailableForRole(column.name)) {
        availableOptions.push({
          label: column.label,
          value: column.name
        });
      }
    });

    // Maneja columnas activadas desde Redux
    if (activatedColumns.length) {
      const activatedOptions = activatedColumns
        .map((columnName) => {
          const column = columns.find((col) => col.name === columnName);
          return column ? { label: column.label, value: column.name } : null;
        })
        .filter(Boolean);
      // Dispatch setActivatedColumns with the activated options values
      dispatch(ganttActions.setActivatedColumns(activatedOptions));

      const allColumnNames = columns
        .filter((col) => !col.ignore_as_column)
        .map((col) => col.name);

      const disabledColumns = allColumnNames.filter(
        (name) => !activatedColumns.includes(name)
      );

      return {
        availableOptions,
        selectedOptions: activatedOptions,
        columnsToDisable: disabledColumns
      };
    } else {
      // Usa configuración por defecto
      const defaultSelectedOptions = columns
        .filter((col) => !col.hide)
        .map((col) => ({ label: col.label, value: col.name }));

      const defaultActivatedNames = columns
        .filter((col) => !col.hide)
        .map((col) => col.name);

      // Actualiza Redux con columnas por defecto
      dispatch(ganttActions.setActivatedColumns(defaultActivatedNames));

      return {
        availableOptions,
        selectedOptions: defaultSelectedOptions,
        columnsToDisable: []
      };
    }
  };

  /**
   * Función principal que configura las columnas de la tabla
   * Orquesta todo el proceso de configuración
   * @returns {Object} Configuración completa de columnas
   */
  const configureTableColumns = () => {
    // 1. Obtener columnas por defecto del sistema
    const defaultColumns = getDefaultColumns();
    // 2. Seleccionar qué columnas usar (preferencias vs defecto)
    const selectedColumns = selectColumnsToUse(defaultColumns);

    // 3. Limpiar problemas en las columnas
    const cleanedColumns = cleanColumns(selectedColumns);

    // 4. Verificar cambios en propiedad filterable
    const hasFilterableChanges = checkFilterableChanges(
      cleanedColumns,
      defaultColumns
    );

    // 5. Restaurar propiedad filterable si es necesario
    const columnsWithCorrectFilterable = restoreFilterableProperty(
      cleanedColumns,
      defaultColumns,
      hasFilterableChanges
    );

    // 6. Aplicar traducciones a las etiquetas
    const translatedColumns = translateColumnLabels(
      columnsWithCorrectFilterable
    );
    // 7. Actualizar estado de Redux
    updateReduxState(translatedColumns);

    // 8. Preparar opciones de columnas para UI
    //const columnOptions = prepareColumnOptions(translatedColumns);

    return {
      columns: translatedColumns
      //...columnOptions
    };
  };

  return {
    configureTableColumns,
    // Exponer funciones individuales para testing o uso específico
    getDefaultColumns,
    selectColumnsToUse,
    cleanColumns,
    checkFilterableChanges,
    restoreFilterableProperty,
    translateColumnLabels,
    updateReduxState,
    prepareColumnOptions
  };
};
